import { useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Calendar, MessageSquare, Crown, ThumbsUp, MessageCircle } from "lucide-react";
import { useLocation } from "wouter";
import { isUnauthorizedError } from "@/lib/authUtils";
import { useToast } from "@/hooks/use-toast";

export default function Community() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  // Fetch community events
  const { data: events, isLoading: eventsLoading } = useQuery({
    queryKey: ["/api/community/events"],
    enabled: !!user,
  });

  // Fetch forum posts
  const { data: forumPosts, isLoading: forumLoading } = useQuery({
    queryKey: ["/api/community/forum"],
    enabled: !!user,
  });

  // Fetch leaderboard
  const { data: leaderboard, isLoading: leaderboardLoading } = useQuery({
    queryKey: ["/api/leaderboard"],
    enabled: !!user,
  });

  // Redirect to home if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      toast({
        title: "Unauthorized",
        description: "You are logged out. Logging in again...",
        variant: "destructive",
      });
      setTimeout(() => {
        window.location.href = "/api/login";
      }, 500);
      return;
    }
  }, [isAuthenticated, isLoading, toast]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-base">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col relative bg-gradient-to-b from-dark-base to-dark-elevated text-white">
      <header className="relative z-10 p-6 flex items-center justify-between">
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLocation("/")}
            className="text-neon-green mr-4"
            data-testid="button-back-home"
          >
            <ArrowLeft size={20} />
          </Button>
          <h1 className="text-2xl font-bold" data-testid="heading-community">Community</h1>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          className="text-gray-400 border-gray-400 hover:bg-gray-400/20"
          onClick={() => window.location.href = '/api/logout'}
          data-testid="button-signout-community"
        >
          Sign Out
        </Button>
      </header>
      
      <main className="relative z-10 flex-1 px-6 pb-8">
        {/* Active Events */}
        <Card className="bg-dark-elevated border-gray-700 mb-6">
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Calendar className="text-coral mr-2" size={20} />
              Live Events & Missions
            </h3>
            
            {eventsLoading ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin w-6 h-6 border-4 border-coral border-t-transparent rounded-full" />
              </div>
            ) : events && events.length > 0 ? (
              <div className="space-y-3" data-testid="events-list">
                {events.map((event: any, index: number) => (
                  <Card 
                    key={event.id} 
                    className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 border-purple-500/30"
                    data-testid={`event-${index}`}
                  >
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium" data-testid={`event-title-${index}`}>{event.title}</span>
                        <span className="bg-purple-500 text-white px-2 py-1 rounded text-xs" data-testid={`event-status-${index}`}>
                          {event.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-300 mb-2" data-testid={`event-description-${index}`}>{event.description}</p>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-purple-400" data-testid={`event-time-${index}`}>
                          {event.endDate ? `Ends ${new Date(event.endDate).toLocaleDateString()}` : 'No end date'}
                        </span>
                        <span className="text-yellow-400" data-testid={`event-reward-${index}`}>
                          Reward: {event.rewardTokens ? `${event.rewardTokens} tokens` : event.rewardNft || 'TBD'}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400" data-testid="empty-events">
                <Calendar size={48} className="mx-auto mb-4 opacity-50" />
                <p>No active events</p>
                <p className="text-sm">Check back soon for new missions!</p>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Community Forum */}
        <Card className="bg-dark-elevated border-gray-700 mb-6">
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <MessageSquare className="text-neon-green mr-2" size={20} />
              Recent Discussions
            </h3>
            
            {forumLoading ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin w-6 h-6 border-4 border-neon-green border-t-transparent rounded-full" />
              </div>
            ) : forumPosts && forumPosts.length > 0 ? (
              <div className="space-y-3" data-testid="forum-posts">
                {forumPosts.map((post: any, index: number) => (
                  <Card key={post.id} className="bg-dark-surface" data-testid={`forum-post-${index}`}>
                    <CardContent className="pt-4">
                      <div className="flex items-center mb-2">
                        <div className="w-8 h-8 bg-coral rounded-full flex items-center justify-center mr-3">
                          <span className="text-white text-sm">
                            {post.user?.username ? post.user.username[0].toUpperCase() : 'U'}
                          </span>
                        </div>
                        <div>
                          <div className="font-medium text-sm" data-testid={`post-author-${index}`}>
                            {post.user?.username || 'Anonymous'}
                          </div>
                          <div className="text-xs text-gray-400" data-testid={`post-time-${index}`}>
                            {new Date(post.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      {post.title && (
                        <h4 className="font-medium mb-1" data-testid={`post-title-${index}`}>{post.title}</h4>
                      )}
                      <p className="text-sm text-gray-300 mb-2" data-testid={`post-content-${index}`}>{post.content}</p>
                      <div className="flex items-center text-xs text-gray-400">
                        <ThumbsUp size={12} className="mr-1" />
                        <span className="mr-3" data-testid={`post-likes-${index}`}>{post.likes || 0}</span>
                        <MessageCircle size={12} className="mr-1" />
                        <span data-testid={`post-replies-${index}`}>{post.replies || 0} replies</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400" data-testid="empty-forum">
                <MessageSquare size={48} className="mx-auto mb-4 opacity-50" />
                <p>No discussions yet</p>
                <p className="text-sm">Be the first to start a conversation!</p>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Leaderboard */}
        <Card className="bg-dark-elevated border-gray-700">
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Crown className="text-yellow-400 mr-2" size={20} />
              Top Explorers
            </h3>
            
            {leaderboardLoading ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin w-6 h-6 border-4 border-yellow-400 border-t-transparent rounded-full" />
              </div>
            ) : leaderboard && leaderboard.length > 0 ? (
              <div className="space-y-2" data-testid="leaderboard">
                {leaderboard.map((player: any, index: number) => {
                  const position = index + 1;
                  const isCurrentUser = player.id === user?.id;
                  
                  return (
                    <div 
                      key={player.id} 
                      className={`flex items-center justify-between rounded-lg p-3 ${
                        position === 1 ? 'bg-yellow-400/10 border border-yellow-400/20' :
                        isCurrentUser ? 'bg-neon-green/10 border border-neon-green/20' : 
                        'bg-dark-surface'
                      }`}
                      data-testid={`leaderboard-${index}`}
                    >
                      <div className="flex items-center">
                        <span 
                          className={`font-bold mr-3 ${
                            position === 1 ? 'text-yellow-400' : 
                            position === 2 ? 'text-gray-300' : 
                            position === 3 ? 'text-coral' : 'text-gray-400'
                          }`}
                          data-testid={`rank-${index}`}
                        >
                          {position}
                        </span>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                          position === 1 ? 'bg-yellow-400' :
                          position === 2 ? 'bg-gray-400' :
                          position === 3 ? 'bg-coral' : 'bg-gray-600'
                        }`}>
                          <span className={`text-sm ${position <= 3 ? 'text-dark-base' : 'text-white'}`}>
                            {player.username ? player.username[0].toUpperCase() : 'U'}
                          </span>
                        </div>
                        <span className="font-medium" data-testid={`player-name-${index}`}>
                          {player.username || 'Anonymous'} {isCurrentUser && '(You)'}
                        </span>
                      </div>
                      <span 
                        className={`font-bold ${
                          position === 1 ? 'text-yellow-400' :
                          position === 2 ? 'text-gray-300' :
                          position === 3 ? 'text-coral' : 'text-gray-400'
                        }`}
                        data-testid={`player-score-${index}`}
                      >
                        {(player.tokens || 0) + (player.discoveries || 0) * 15} XP
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400" data-testid="empty-leaderboard">
                <Crown size={48} className="mx-auto mb-4 opacity-50" />
                <p>No rankings yet</p>
                <p className="text-sm">Start exploring to join the leaderboard!</p>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
